import { useState, useEffect, useContext } from "react";
import { Menu, ShoppingCart, X, ChevronDown, ChevronUp } from "lucide-react";
import Search from '../../Assets/Search.png'
import "./Navbar.css";
import { Link, useNavigate } from "react-router-dom";
import title from "../../Assets/title.png";
import axios from "axios";
import { ShopContext } from "../../Context/ShopContext"; // Adjust this import path to match your project structure

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  // Get cart from context
  const { cart } = useContext(ShopContext);
  
  // Calculate total items in cart
  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  // Fetch categories and collections from backend hehehe
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Use Promise.all to fetch data in parallel
        const [categoriesRes, collectionsRes] = await Promise.all([
          axios.get("https://sc-backend-9nhi.onrender.com/api/products/categories"),
          axios.get("https://sc-backend-9nhi.onrender.com/api/products/collections")
        ]);
        
        // Set the fetched data to state
        setCategories(categoriesRes.data || []);
        setCollections(collectionsRes.data || []);
        
        // Default materials (since these are hardcoded in Collection.jsx)
        setMaterials(["Cotton", "Silk", "Satin", "Denim"]);
      } catch (error) {
        console.error("Error fetching navbar data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setDropdownOpen(null);
  };

  // Navigate to collection with proper URL encoding for filters
  const navigateToFilter = (filterType, value) => {
    // Properly encode the filter value to handle spaces
    const encodedValue = encodeURIComponent(value);
    navigate(`/collection?${filterType}=${encodedValue}`);
    closeMenu();
  };

  const handleSearchClick = () => {
    // Redirect to collection page with search parameter to trigger search bar
    navigate('/collection?search=true');
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-container">
          <button onClick={() => setIsOpen(true)} className="menu-icon">
            <Menu size={24} />
          </button>
          
          <div className="logo">
            <Link to="/">
              <img src={title} alt="Store Logo" />
            </Link>
          </div>
          
          <div className="nav-icons">
            <button onClick={handleSearchClick} className="home-search-icon">
              <img src={Search} />
            </button>
            <Link to="/cart" className="cart-icon-container">
              <ShoppingCart size={24} />
              {cartItemCount > 0 && (
                <span className="cart-count">{cartItemCount}</span>
              )}
            </Link>
          </div>
        </div>
        
        {isOpen && (
          <div className="overlay" onClick={closeMenu}>
            <div className="sidebar" onClick={(e) => e.stopPropagation()}>
              <button onClick={closeMenu} className="close-btn">
                <X size={24} />
              </button>
              <ul className="menu">
                <li><Link to="/" onClick={closeMenu}>Home</Link></li>
                <li><Link to="/collection" onClick={closeMenu}>The Complete Edit</Link></li>

                {/* Categories Menu */}
                <li>
                  <button className="dropdown-btn" onClick={() => toggleDropdown(1)}>
                    Categories {dropdownOpen === 1 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {dropdownOpen === 1 && (
                    <ul className="submenu">
                      {isLoading ? (
                        <li>Loading categories...</li>
                      ) : categories.length > 0 ? (
                        categories.map((category, index) => (
                          <li key={`category-${index}`}>
                            <Link 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                navigateToFilter("Category", category);
                              }}
                            >
                              {category}
                            </Link>
                          </li>
                        ))
                      ) : (
                        <li>No categories available</li>
                      )}
                    </ul>
                  )}
                </li>

                {/* Collections Menu */}
                <li>
                  <button className="dropdown-btn" onClick={() => toggleDropdown(2)}>
                    Collections {dropdownOpen === 2 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {dropdownOpen === 2 && (
                    <ul className="submenu">
                      {isLoading ? (
                        <li>Loading collections...</li>
                      ) : collections.length > 0 ? (
                        collections.map((collection, index) => (
                          <li key={`collection-${index}`}>
                            <Link 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                navigateToFilter("Collection", collection);
                              }}
                            >
                              {collection}
                            </Link>
                          </li>
                        ))
                      ) : (
                        <li>No collections available</li>
                      )}
                    </ul>
                  )}
                </li>

                {/* Materials Menu */}
                <li>
                  <button className="dropdown-btn" onClick={() => toggleDropdown(3)}>
                    Material {dropdownOpen === 3 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {dropdownOpen === 3 && (
                    <ul className="submenu">
                      {materials.map((material, index) => (
                        <li key={`material-${index}`}>
                          <Link 
                            to="#" 
                            onClick={(e) => {
                              e.preventDefault();
                              navigateToFilter("Material", material);
                            }}
                          >
                            {material}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
                <li><Link to="/customize" onClick={closeMenu}>Design Your Piece</Link></li>
                <li><Link to="/about" onClick={closeMenu}>Who we are</Link></li>
                <li><Link to="/blogs" onClick={closeMenu}>Blogs</Link></li>
              </ul>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Navbar;
